import React, { Component } from 'react'
import { connect } from 'react-redux'
import { compose } from 'redux'
import { withRouter } from 'react-router-dom'
import { href } from '@licnz/js-utils'
import queryString from 'query-string'

import { enqueueError } from 'lib/components/GlobalNotifications'
import { enqueueFlashNotification } from 'lib/components/FlashNotifications/actions/flashNotificationsActions'
import {
  customerContextIsComplete,
  canFetchBasket,
} from 'selectors/customerContextSelectors'
import { fetchBasket } from 'actions/basketActions'
import { fetchBillingEntities } from 'actions/billingEntitiesActions'
import { fetchBillingEntity } from 'actions/billingEntityActions'
import { setCustomerContext } from 'actions/customerContextActions'
import { fetchOperation } from 'actions/operationActions'
import { fetchOperations, searchOperations } from 'actions/operationsActions'
import { fetchOrganisation } from 'actions/organisationActions'
import { fetchPerson } from 'actions/personActions'
import { isImpersonating } from 'selectors/impersonationSelectors'
import { retryFetch } from 'utils/util'

import Button from 'lib/components/Button'
import Dropdown from 'lib/components/Dropdown'
import ReactModal from 'react-modal'

import styles from './styles.scss'

const customStyles = {
  overlay: {
    animation: 'modal-in 0.15s ease-out 0s backwards',
    backgroundColor: 'rgba(0, 30, 98, .5)',
    zIndex: '4',
  },
}

class CustomerContextHandler extends Component {
  constructor(props) {
    super(props)

    this.state = {
      mustSelectOperation: false,
      selectedOperation: null,
    }

    this.handleSelectOperation = this.handleSelectOperation.bind(this)
    this.handleSubmit = this.handleSubmit.bind(this)
  }

  componentDidUpdate(prevProps) {
    let {
      basket,
      billingEntities,
      canFetchBasket,
      customerContext,
      customerContextIsComplete,
      enqueueError,
      enqueueFlashNotification,
      fetchBillingEntities,
      fetchOperations,
      isImpersonating,
      operations,
      requiresOperation,
      searchOperations,
      location: { search },
    } = this.props

    const message = "Please try again. If problem persists, please contact LIC on 0800 2 MINDA (0800 264 632)."
    const ctaLabel = isImpersonating ? 'Back to customer search' : 'Reload page'
    const ctaLink = isImpersonating ? global.config.LIC_CUSTOMER_ENDPOINT : global.config.SHOP_FRONTEND_ENDPOINT

    // organisation_party_link is fetched:
    // * in CustomerHandler using the user's profile data and their selected org if user has relationship with more than 1 org
    // * in FsmHandler using the organisation_id from the url params from Customer app
    if (
      !prevProps.customerContext &&
      customerContext &&
      customerContext.organisation_party_link
    ) {
      let organisationLink = customerContext.organisation_party_link

      // POST account/billing_entities/search
      retryFetch(() => fetchBillingEntities(organisationLink))
        .catch(() => {
          enqueueError({
            message: "There was a problem fetching the billing entities. You won't be able to add products to your basket. " + message,
            ctaLabel,
            ctaLink,
          })
        })

      if (isImpersonating) {
        // NOTE AgriManagers use a different API to customers to avoid finding operations via transitive associations
        // Customers do not have access to this API, but the API they use is scoped by their auth token,
        // avoiding the transitive association problem
        // POST relationship/api/v1/relationships/search
        retryFetch(() => searchOperations(organisationLink))
          .catch(() =>
            enqueueError({
              message: "There was a problem fetching the animal group details. You won't be able to add products to your basket. " + message,
              ctaLabel,
              ctaLink,
            })
          )
      } else {
        // POST relationship/api/v1/parties/match
        retryFetch(() => fetchOperations(organisationLink))
          .catch(() =>
            enqueueError({
              message: "There was a problem fetching the herd details. You won't be able to add products to your basket. " + message,
              ctaLabel,
              ctaLink,
            })
          )
      }
    }

    if (!prevProps.canFetchBasket && canFetchBasket) {
      this.getBasket(message, ctaLabel, ctaLink)
    }

    // Set additional customer context from basket
    if (!prevProps.basket && basket) {
      let billingEntity = basket['urn:lic:predicate:bill_to_party'].identifier.identifier
      this.setBillingEntity(billingEntity)

      let operationParty = basket['urn:lic:predicate:assign_to_party']
      if (operationParty && Object.keys(operationParty).length)
        this.setOperation(operationParty.identifier.identifier)
    }

    let flashNotificationMessages = []

    // This condition is met when:
    // * fetchBillingEntities request above found no billing entities
    // * user is staff and UserHandler fetchPermissions request failed
    if (prevProps.billingEntities !== billingEntities && !customerContext.bill_to_link) {
      // An Organisation will only ever have 0 or 1 billing entities
      if (billingEntities.length === 0) {
        enqueueError({
          message: 'No billing entity found. ' + message,
          ctaLabel,
          ctaLink,
        })
        flashNotificationMessages.push('No billing entity found.')
      } else this.setBillingEntity(href({ links: billingEntities[0].links, rel: 'self' }))
    }

    let operationLink = queryString.parse(search).operation_id
    if (
      operationLink &&
      customerContext &&
      operationLink !== customerContext.assign_to_link
    ) {
      this.setOperation(operationLink)
    }

    // Dereference the `assign_to_link` when one is set
    if (
      customerContext &&
      customerContext.assign_to_link &&
      customerContext.assign_to_link !==
        (prevProps.customerContext && prevProps.customerContext.assign_to_link)
    ) {
      this.dereferenceAssignToParty(message, ctaLabel, ctaLink)
    }

    // This condition is met when:
    // * searchOperations/fetchOperations request above found no operations
    // * user is staff and UserHandler fetchPermissions request failed
    if (prevProps.operations !== operations && !operations.length) {
      const msg = isImpersonating ? 'No animal groups found.' : 'No herds found.'
      enqueueError({
        message: `${msg} ${message}`,
        ctaLabel,
        ctaLink,
      })
      flashNotificationMessages.push(msg)
    }

    if (flashNotificationMessages.length) {
      let linkClasses = `${styles.button} ${styles.buttonPrimary}`
      let detailComponent = (
        <p>
          {flashNotificationMessages.join(' ')} {' '} {message} {' '}
          <a href={ctaLink} className={linkClasses}>
            {ctaLabel}
          </a>
        </p>
      )
      let id = 'customer_search' + Math.random()
      enqueueFlashNotification({ id: id, detailComponent })
    }

    if (!prevProps.customerContextIsComplete && customerContextIsComplete) {
      this.dereferenceBaseContext(message, ctaLabel, ctaLink)
    }

    if (
      (operations.length && !prevProps.requiresOperation && requiresOperation) ||
      (prevProps.operations !== operations && requiresOperation)
    ) {
      if (!customerContext.assign_to_link) {
        if (operations.length === 1)
          this.setOperationParameter(href({ links: operations[0].links, rel: 'self' }))
        else this.setState({ mustSelectOperation: true })
      }
    }

    if (
      prevProps.customerContext &&
      !prevProps.customerContext.assign_to_link &&
      customerContext.assign_to_link
    ) {
      this.setState({ mustSelectOperation: false })
    }
  }

  dereferenceBaseContext(message, ctaLabel, ctaLink) {
    let {
      customerContext,
      enqueueError,
      fetchBillingEntity,
      fetchOrganisation,
      fetchPerson,
    } = this.props

    // GET relationship/api/v1/parties/:id
    retryFetch(() => fetchOrganisation(customerContext.organisation_party_link))
      .catch(() => {
        enqueueError({
          message: "There was a problem fetching the organisation details. You won't be able to add products to your basket. " + message,
          ctaLabel,
          ctaLink,
        })
      })

    // GET relationship/api/v1/parties/:id
    retryFetch(() => fetchPerson(customerContext.party_link))
      .catch(() => {
        enqueueError({
          message: "There was a problem fetching the person details. You won't be able to place an order. " + message,
          ctaLabel,
          ctaLink,
        })
      })

    // GET /account/billing_entities/:id
    retryFetch(() => fetchBillingEntity(customerContext.bill_to_link))
      .catch(() => {
        enqueueError({
          message: "There was a problem fetching the billing entity. You won't be able to place an order. " + message,
          ctaLabel,
          ctaLink,
        })
      })
  }

  dereferenceAssignToParty(message, ctaLabel, ctaLink) {
    let { customerContext, fetchOperation, isImpersonating, enqueueError } = this.props
    let operationPartyLink = customerContext.assign_to_link

    // GET relationship/api/v1/parties/:id
    retryFetch(() => fetchOperation(operationPartyLink))
      .catch(() => {
        const term = isImpersonating ? 'animal group' : 'herd'
        enqueueError({
          message: `There was a problem fetching the ${term}. You won't be able to add products to your basket. ${message}`,
          ctaLabel,
          ctaLink,
        })
      })
  }

  getBasket(message, ctaLabel, ctaLink) {
    let { customerContext, fetchBasket, enqueueError } = this.props

    // GET order/basket/find which retrieves the latest basket scoped by organisation
    // (purchasing_party_link) and operation (assign_to_party_link)
    retryFetch(() => fetchBasket(customerContext), [404])
      .then(response => {
        if (!customerContext.bill_to_link) {
          let billingEntityLink =
            response.data['urn:lic:predicate:bill_to_party'].identifier.identifier
          this.setBillingEntity(billingEntityLink)
        }
      })
      .catch((err) => {
        // 404 is expected response if the customer has no items already in basket. A new basket
        // will be created when items are added to basket.
        if (!(err.response?.status === 404)) {
          enqueueError({
            message: "There was a problem fetching your basket. You won't be able to add products to basket. " + message,
            ctaLabel,
            ctaLink,
          })
        }
      })
  }

  handleSelectOperation(operationLink) {
    this.setState({ selectedOperation: operationLink })
  }

  handleSubmit() {
    let { selectedOperation } = this.state
    this.setOperationParameter(selectedOperation)
  }

  setBillingEntity(billingEntityLink) {
    let { customerContext, setCustomerContext } = this.props
    setCustomerContext({ ...customerContext, bill_to_link: billingEntityLink })
  }

  setOperation(operationLink) {
    let { customerContext, setCustomerContext } = this.props
    setCustomerContext({ ...customerContext, assign_to_link: operationLink })
  }

  setOperationParameter(operationLink) {
    let {
      history,
      location: { search },
    } = this.props

    let params = queryString.parse(search)
    params.operation_id = operationLink
    let query = `?${queryString.stringify(params)}`
    history.replace(query)
  }

  render() {
    let { selectedOperation } = this.state
    let { organisation, person } = this.props
    let operationOptions = this.props.operations.map(op => {
      let name = `${op.metadata.name} (${op.metadata.handle})`
      return { id: href({ links: op.links, rel: 'self' }), name }
    })

    return this.state.mustSelectOperation &&
      operationOptions.length &&
      person &&
      organisation ? (
      <ReactModal
        isOpen={this.state.mustSelectOperation}
        contentLabel='Operation Select'
        className={styles.content}
        style={customStyles}
        ariaHideApp={false}
      >
        <p>
          {'Purchasing on behalf of ' +
            person.metadata.name +
            ' for ' +
            organisation.metadata.name}
        </p>
        <p>Which Operation would you like to order for?</p>
        <Dropdown
          onSelect={this.handleSelectOperation}
          options={operationOptions}
          selectedOptionId={selectedOperation}
          selectOptionText='Select an operation...'
        />
        <Button className='buttonSecondary buttonBlock' onClick={this.handleSubmit}>
          Continue shopping
        </Button>
      </ReactModal>
    ) : null
  }
}

const mapDispatchToProps = {
  enqueueError,
  enqueueFlashNotification,
  fetchBasket,
  fetchBillingEntities,
  fetchBillingEntity,
  fetchOperation,
  fetchOperations,
  fetchOrganisation,
  fetchPerson,
  searchOperations,
  setCustomerContext,
}

export { CustomerContextHandler }
export default compose(
  connect(state => {
    return {
      basket: state.basket.data,
      billingEntities: state.billingEntities.data,
      canFetchBasket: canFetchBasket(state),
      customerContext: state.customerContext.data,
      customerContextIsComplete: customerContextIsComplete(state),
      isImpersonating: isImpersonating(state),
      operations: state.operations.data,
      organisation: state.customerContext.organisation.data,
      person: state.customerContext.person.data,
      requiresOperation: state.customerContext.requiresOperation,
    }
  }, mapDispatchToProps),
  withRouter
)(CustomerContextHandler)
